import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Wallet as WalletIcon,
  IndianRupee,
  RefreshCw,
  TrendingUp,
  CreditCard,
  ArrowDownRight,
  ArrowUpRight,
  Receipt,
  Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';

const Wallet = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { get } = useApi();
  const { user } = useAuth();

  useEffect(() => {
    fetchWalletData();
  }, []);

  // Get admin ID from user object
  const getAdminId = () => {
    return user?.id || user?._id || user?.admin_id || user?.userId || user?.user_id || null;
  };

  const fetchWalletData = async () => {
    try {
      setIsLoading(true);
      const adminId = getAdminId();
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add admin_id if available
      if (adminId) {
        params.append('admin_id', adminId);
      }
      
      const queryString = params.toString();
      const endpoint = `/admin/wallet/dashboard${queryString ? `?${queryString}` : ''}`;
      
      const response = await get(endpoint);
      setWalletData(response);
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const normalizeAmount = (value) => {
    const numericValue =
      typeof value === 'number' ? value : parseFloat((value ?? '').toString());
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTitleCase = (value) => {
    if (!value) return 'N/A';
    return value
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'AD';
  };

  const transactions = useMemo(() => {
    const rechargeList = walletData?.recent_recharges || [];
    const cashbackList = walletData?.recent_cashback || [];

    if (!rechargeList.length && !cashbackList.length) {
      return [];
    }

    const rechargeMap = new Map(
      rechargeList.map((recharge) => [recharge.txn_id, { ...recharge }])
    );

    const enrichedRecharges = [...rechargeMap.values()];

    cashbackList.forEach((cashback) => {
      const txnId = cashback.meta?.txn_id;
      if (!txnId) {
        return;
      }
      if (!rechargeMap.has(txnId)) {
        enrichedRecharges.push({
          txn_id: txnId,
          amount: cashback.meta?.amount ?? cashback.amount,
          provider: formatTitleCase(cashback.meta?.source || cashback.reason),
          mobile_number: cashback.meta?.mobile_number ?? 'N/A',
          created_at: cashback.created_at,
          source: cashback.meta?.source,
        });
      }
    });

    return enrichedRecharges
      .sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      )
      .map((recharge) => ({
        recharge,
        cashback: cashbackList.find((cb) => cb.meta?.txn_id === recharge.txn_id),
      }));
  }, [walletData]);

  const transactionColorVariants = [
    {
      container: 'border-1 border-muted-foreground/20 bg-rose-500/5',
      header: 'bg-gradient-to-r from-rose-500 to-rose-400 text-white',
      inactiveButton:
        'border border-rose-500/30 bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 hover:text-rose-700',
    },
    {
      container: 'border-1 border-muted-foreground/20 bg-emerald-500/5',
      header: 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-white',
      inactiveButton:
        'border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-700',
    },
    {
      container: 'border-1 border-muted-foreground/20 bg-sky-500/5',
      header: 'bg-gradient-to-r from-sky-500 to-sky-400 text-white',
      inactiveButton:
        'border border-sky-500/30 bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 hover:text-sky-700',
    },
    {
      container: 'border-1 border-muted-foreground/20 bg-amber-500/5',
      header: 'bg-gradient-to-r from-amber-500 to-amber-400 text-white',
      inactiveButton:
        'border border-amber-500/30 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 hover:text-amber-700',
    },
    {
      container: 'border-1 border-muted-foreground/20 bg-primary/5',
      header: 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground',
      inactiveButton:
        'border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary',
    },
  ];

  const displayName = user?.name || 'Admin User';
  const displayEmail = user?.email || user?.username || 'admin@example.com';
  const balanceDisplay = formatCurrency(normalizeAmount(walletData?.admin_wallet?.balance));

  // Enriched recharges for desktop view (combines recharges + cashback-derived entries)
  const enrichedRechargesForDesktop = useMemo(() => {
    const rechargeList = walletData?.recent_recharges || [];
    const cashbackList = walletData?.recent_cashback || [];

    const rechargeMap = new Map(
      rechargeList.map((recharge) => [recharge.txn_id, { ...recharge }])
    );

    const enriched = [...rechargeMap.values()];

    // Add cashback entries that don't have corresponding recharge records
    cashbackList.forEach((cashback) => {
      const txnId = cashback.meta?.txn_id;
      if (txnId && !rechargeMap.has(txnId)) {
        enriched.push({
          txn_id: txnId,
          amount: cashback.meta?.amount ?? cashback.amount,
          provider: formatTitleCase(cashback.meta?.source || cashback.reason),
          mobile_number: cashback.meta?.mobile_number ?? 'N/A',
          created_at: cashback.created_at,
          source: cashback.meta?.source,
          commission_amount: parseFloat(cashback.amount) || 0,
        });
      }
    });

    return enriched.sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }, [walletData]);

  const buildTransactionDetails = (txn) => {
    if (!txn) {
      return [];
    }

    return [
      {
        label: 'Transaction ID',
        value: txn.recharge?.txn_id || txn.cashback?.meta?.txn_id || 'N/A',
      },
      {
        label: 'Recharge Amount',
        value: txn.recharge?.amount
          ? formatCurrency(normalizeAmount(txn.recharge.amount))
          : txn.cashback?.meta?.amount
            ? formatCurrency(normalizeAmount(txn.cashback.meta.amount))
            : formatCurrency(0),
      },
      {
        label: 'Provider',
        value:
          txn.recharge?.provider ||
          (txn.cashback?.meta?.source ? formatTitleCase(txn.cashback.meta.source) : 'N/A'),
      },
      {
        label: 'Mobile Number',
        value: txn.recharge?.mobile_number || 'N/A',
      },
      {
        label: 'Recent Cashback',
        value: txn.cashback
          ? formatCurrency(normalizeAmount(txn.cashback.amount))
          : 'No Cashback',
      },
      {
        label: 'Cashback Type',
        value: txn.cashback ? formatTitleCase(txn.cashback.type) : 'No Cashback',
      },
      {
        label: 'Source',
        value: txn.cashback?.meta?.source
          ? formatTitleCase(txn.cashback.meta.source)
          : txn.recharge?.source
            ? formatTitleCase(txn.recharge.source)
            : 'N/A',
      },
    ];
  };

  useEffect(() => {
    if (transactions.length) {
      setSelectedIndex(0);
    }
  }, [transactions.length]);

  const renderSummary = () => (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <WalletIcon className="h-5 w-5 text-foreground" />
          <h2 className="text-xl font-bold text-foreground">Wallet Dashboard</h2>
        </div>
        <Button
          variant="outline"
          onClick={fetchWalletData}
          disabled={isLoading}
          className="border-1 border-muted-foreground/20 text-foreground "
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          
        </Button>
      </div>
    </>
  );

  const renderMobileBalanceCard = () => (
    <Card className="border-1 border-muted-foreground/20 bg-background/70 backdrop-blur-sm shadow-sm lg:hidden">
      <CardContent className="p-4 flex items-center justify-between gap-4">
        {isLoading ? (
          <div className="w-full flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-12 w-12 rounded-full" />
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Current Balance</p>
              <div className="flex items-center gap-1 text-lg font-semibold text-foreground">
                <IndianRupee className="h-4 w-4" />
                <span>{balanceDisplay}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Currency: {walletData?.admin_wallet?.currency || 'INR'}
              </p>
            </div>
            <Avatar className="h-12 w-12 border-2 border-primary/30 shadow-sm">
              <AvatarImage src={user?.avatar} alt={displayName} />
              <AvatarFallback className="text-sm font-semibold text-foreground">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderMobileTransactions = () => {
    if (isLoading) {
      return (
        <Card className="border-1 border-muted-foreground/20">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-11/12" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      );
    }

    if (!transactions.length) {
      return (
        <Card className="border-1 border-muted-foreground/20">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No recharge transactions available yet.
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-2">
        {transactions.map((txn, idx) => {
          const labelNumber = transactions.length - idx;
          const colorVariant =
            transactionColorVariants[idx % transactionColorVariants.length];
          const isSelected = idx === selectedIndex;
          const details = buildTransactionDetails(txn);

          const detailContainerClasses = isSelected
            ? 'opacity-100 translate-y-0 scale-100 max-h-[480px] pointer-events-auto'
            : 'opacity-0 -translate-y-2 scale-95 max-h-0 pointer-events-none';

          return (
            <Card
              key={txn.recharge?.txn_id || txn.cashback?.meta?.txn_id || idx}
              className={`relative cursor-pointer transition-all duration-300 ease-out rounded-3xl ${colorVariant.container} ${
                isSelected
                  ? 'z-20 shadow-xl scale-[1.01]'
                  : 'z-10 opacity-80 hover:opacity-100 hover:scale-[1.01]'
              }`}
              onClick={() => setSelectedIndex(idx)}
            >
              <div
                className={`rounded-t-2xl rounded-b-xl px-4 py-3 text-sm font-semibold ${
                  colorVariant.header
                }`}
              >
                Transaction -{labelNumber}
              </div>

              <div
                className={`px-4 transition-all duration-300 ease-out overflow-hidden ${detailContainerClasses}`}
                aria-hidden={!isSelected}
              >
                <div className="py-3 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 transition-transform duration-300 ease-out">
                      <AvatarImage src={user?.avatar} alt={displayName} />
                      <AvatarFallback className="font-semibold">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-base font-semibold text-foreground">{displayName}</p>
                      <p className="text-sm text-muted-foreground">{displayEmail}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {details.map((row) => (
                      <div
                        key={`${row.label}-${row.value}`}
                        className={`flex items-center justify-between text-sm sm:text-base transition-all duration-300 ${
                          isSelected ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                        }`}
                      >
                        <span className="text-muted-foreground">{row.label} :</span>
                        <span className="font-semibold text-foreground text-right">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderDesktopDashboard = () => (
    <div className="p-8 space-y-6 bg-gradient-to-br from-background via-slate-900/5 to-primary/5 rounded-3xl border-1 border-muted-foreground/20">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <WalletIcon className="h-5 w-5" />
            Wallet Dashboard
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your wallet balance and recent activities
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchWalletData}
          disabled={isLoading}
          className="border-1 border-primary/20 text-foreground hover:bg-primary/10"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="relative overflow-hidden bg-gradient-to-r from-primary/10 via-background to-primary/5 border-1 border-muted-foreground/20 backdrop-blur-xl shadow-xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-30"></div>
        <CardContent className="p-6 flex items-center justify-between gap-6 relative z-10">
          {isLoading ? (
            <div className="w-full flex justify-between items-center">
              <div className="space-y-4">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-20 w-20 rounded-full" />
            </div>
          ) : (
            <>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Balance</p>
                <div className="flex items-baseline gap-1">
                  <IndianRupee className="h-4 w-4" />
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">
                    {formatCurrency(normalizeAmount(walletData?.admin_wallet?.balance))}
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Currency: {walletData?.admin_wallet?.currency || 'INR'}
                </p>
              </div>

              <Avatar className="h-20 w-20 border-4 border-primary/30 shadow-lg">
                <AvatarImage src={user?.avatar} alt={displayName} />
                <AvatarFallback className="text-foreground font-semibold">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
            </>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        <Card className="border-1 border-muted-foreground/20 bg-background/60 backdrop-blur-xl hover:shadow-xl transition-all duration-300 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <TrendingUp className="text-green-500 h-5 w-5" />
              Recent Cashbacks
            </CardTitle>
            <CardDescription>Track your recent cashback credits</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : walletData?.recent_cashback?.length ? (
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {walletData.recent_cashback.map((item, index) => {
                  const isCredit = item.type === 'credit';
                  return (
                    <div
                      key={item.id || index}
                      className={`group p-4 border-1 border-muted-foreground/20 rounded-xl bg-gradient-to-r from-green-50/10 to-transparent hover:from-green-100/10 hover:shadow-lg transition-all duration-300 flex justify-between items-start`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            isCredit ? 'bg-green-500' : 'bg-red-500'
                          } text-white`}
                        >
                          {isCredit ? (
                            <ArrowDownRight className="h-4 w-4" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-foreground">
                            {formatTitleCase(item.reason)}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(item.created_at)}
                          </p>
                          {item.meta?.txn_id && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Transaction:{' '}
                              <span className="font-medium">{item.meta.txn_id}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className={`text-right font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {`${isCredit ? '+' : '-'}${formatCurrency(normalizeAmount(item.amount))}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-10">
                No cashback records found.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-1 border-muted-foreground/20 bg-background/60 backdrop-blur-xl hover:shadow-xl transition-all duration-300 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <CreditCard className="text-blue-500 h-5 w-5" />
              Recent Recharges
            </CardTitle>
            <CardDescription>Latest mobile recharge transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : enrichedRechargesForDesktop?.length ? (
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {enrichedRechargesForDesktop.map((recharge, index) => (
                  <div
                    key={recharge.txn_id || index}
                    className="p-4 border-1 border-muted-foreground/20 rounded-xl bg-gradient-to-r from-blue-50/10 to-transparent hover:from-blue-100/10 hover:shadow-lg transition-all duration-300 flex justify-between items-start"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-blue-500 text-white">
                        <Receipt className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground">
                          {formatTitleCase(recharge.provider)}
                        </h4>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Smartphone className="h-3 w-3" />
                          {recharge.mobile_number}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(recharge.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground">
                        {formatCurrency(normalizeAmount(recharge.amount))}
                      </p>
                      {recharge.commission_amount && (
                        <p className="text-xs text-green-600 mt-1">
                          +{formatCurrency(normalizeAmount(recharge.commission_amount))} Cashback
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-10">
                No recharge records found.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-6 p-4 sm:p-6 rounded-3xl lg:hidden">
        {renderSummary()}
        {renderMobileBalanceCard()}
        {renderMobileTransactions()}
      </div>

      <div className="hidden lg:block">
        {renderDesktopDashboard()}
      </div>
    </>
  );
};

export default Wallet;
